<template>
    <div class="executive-summary">
        <div class="summary-header">
            <h2>Executive Summary</h2>
            <p class="summary-description">Network wide performance metrics</p>
        </div>

        <h3 class="summary-group-title">In this range</h3>
        <div class="summary-grid">
            <MetricCard title="Total Candidates" :value="summary?.applicants?.totalCandidates"
                :subtitle="`${summary?.applicants?.uniqueCandidates} unique`" :icon="UsersIcon" variant="info"
                format="number" />

            <MetricCard title="Total Hires" :value="summary?.hires?.totalHires"
                :subtitle="`${summary?.hires?.uniqueHires} unique`" :icon="UserPlusIcon" variant="success"
                format="number" />

            <MetricCard title="Avg Time to Hire" :value="summary?.avgTimeToHire" footer="days to complete hiring"
                :icon="ClockIcon" :variant="timeToHireVariant" format="days" />

            <MetricCard title="Candidate No-Show Rate" :value="summary?.interviewNoShows?.candidateNoShowRate"
                :subtitle="`${summary?.interviewNoShows?.candidateNoShows} of ${summary?.interviewNoShows?.totalInterviews} interviews · ${summary?.interviewNoShows?.totalInterviewHours ?? 0} hrs`"
                :icon="CalendarDaysIcon" :variant="noShowVariant" format="percent" />
        </div>

        <h3 class="summary-group-title">Right now</h3>
        <div class="summary-grid">
            <MetricCard title="Open Roles" :value="summary?.openRoles" footer="roles not yet filled"
                :icon="BriefcaseIcon" variant="info" format="number" />

            <MetricCard title="Aging Candidates" :value="summary?.agingCandidates" footer="needs attention"
                :icon="ExclamationTriangleIcon" :variant="agingCandidatesVariant" format="number" />
        </div>
    </div>
</template>

<script setup>

import { computed } from "vue";
import MetricCard from "./MetricCard.vue";
import { UsersIcon, UserPlusIcon, ClockIcon, BriefcaseIcon, ExclamationTriangleIcon, CalendarDaysIcon } from "@heroicons/vue/24/outline";

const props = defineProps({
  summary: {
    type: Object,
    required: true
  }
});

const timeToHireVariant = computed(() => {
  const days = props.summary?.avgTimeToHire;
  if (!days) return "default";
  if (days <= 15) return "success";
  if (days <= 20) return "warning";
  return "danger";
});

const agingCandidatesVariant = computed(() => {
  const count = props.summary?.agingCandidates;
  const total = props.summary?.applicants?.totalCandidates;
  if (!count || !total) return "default";

  const ratio = count / total;
  if (ratio < 0.2) return "success";
  if (ratio < 0.4) return "warning";
  return "danger";
});

const noShowVariant = computed(() => {
  const rate = props.summary?.interviewNoShows?.candidateNoShowRate;
  if (!rate) return "default";
  if (rate <= 5) return "success";
  if (rate <= 10) return "warning";
  return "danger";
});

</script>
<style scoped>
.executive-summary {
    background: white;
    border-radius: 12px;
    padding: 2rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    width: 100%;
}

.summary-header {
    margin-bottom: 2rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid #e5e7eb;
}

.summary-header h2 {
    margin: 0 0 0.5rem 0;
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
}

.summary-description {
    margin: 0;
    font-size: 0.875rem;
    color: #6b7280;
}

.summary-group-title {
    margin: 0 0 1rem;
    font-size: 0.8125rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #6b7280;
}

.summary-grid + .summary-group-title {
    margin-top: 2rem;
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
}

@media (min-width: 1400px) {
    .summary-grid {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (max-width: 768px) {
    .executive-summary {
        padding: 1rem;
        border-radius: 8px;
    }

    .summary-header {
        margin-bottom: 1.5rem;
    }

    .summary-grid {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
}
</style>
